import async from 'async';
import _ from 'lodash';
import storage from '@/renderer/scripts/utils/storage';
import TaskManager from '@/renderer/scripts/utils/TaskManager/BaseTaskManager';
import { SubTask, Task, TaskStatus } from '@/renderer/scripts/utils/TaskManager/types';
import { getSpaceAndUserInfo } from '@/renderer/scripts/utils/user/getUserSpaceInfo';
import { SMH } from '@/renderer/scripts/smh';
import { httpUrl } from '@/renderer/scripts/http';
import getRequest from '@/renderer/scripts/httpRequest';
import { TRANSFER_LIMIT, TransferSetting } from '@/renderer/scripts/utils/ConstantConfig';
import { FileInfo } from '@/renderer/scripts/utils/upload/types';
import { transformErrorForSerialization } from '@/common/tools';

import { TaskStatusUtil } from '@/renderer/scripts/utils/TaskManager/utils/TaskUtils';
import { BaseTaskManagerHook } from '@/renderer/scripts/utils/TaskManager/types/hooks';
import { CommonLoader } from '@/renderer/scripts/utils/CommonLoader';
import Downloader from './Downloader';
import { DownloadFileInfo, ProgressInfo } from './types';

const settingKey = storage.StoreKey.DOWNLOAD_TRANSFER_SETTING; // 上传相关系统配置-key
export const downloadOptions = {
  type: 'download',
  batchLimit: storage.get<TransferSetting>(settingKey).batchLimit || TRANSFER_LIMIT.download.batchLimit,
  onStart(params: BaseTaskManagerHook<DownloadFileInfo>) {
    const { task, onProgress, onFinish, onError } = params;
    task.successNum = task.successNum || 0;
    task.errorNum = task.errorNum || 0;
    task.totalNum = task.getListLength();

    // 子任务容量池大小
    const subTaskPool: number = this.batchLimit - (this as unknown as TaskManager<FileInfo>).getRunningSubTaskMapSize();
    // 若子任务容量池还很大，则只开启对应的文件长度的并发（减少消耗）
    // 若子任务容量池还很大，则可以用所有的容量池进行遍历
    const subTaskLimit = Math.min(subTaskPool, task.getListLength());
    async.forEachLimit(
      task.getList(),
      subTaskLimit,
      (subTask, next) => {
        const removeEvents = () => {
          subTask.ins &&
            subTask.ins
              .off('hashProgress', hashProgressFn)
              .off('progress', progressFn)
              .off('status_changed', statusChangedFn)
              .off('success', successFn)
              .off('error', errorFn);
        };
        const bindEvnets = () => {
          subTask.ins &&
            subTask.ins
              .on('hashProgress', hashProgressFn)
              .on('progress', progressFn)
              .on('status_changed', statusChangedFn)
              .once('success', successFn)
              .once('error', errorFn);
        };

        const hashProgressFn = (progressInfo: ProgressInfo) => {
          subTask.hashPercent = progressInfo.percent;
          subTask.speed = progressInfo.speed;
          onProgress();
        };

        const progressFn = (progressInfo: ProgressInfo) => {
          const { percent, speed, remainingTime, remainingMessage, loaded } = progressInfo;
          // 子任务进行数据绑定
          subTask.percent = percent;
          subTask.loaded = loaded;
          subTask.speed = speed;
          subTask.remainingTime = remainingTime;
          subTask.remainingMessage = remainingMessage;
          const totalSpeed = (this as unknown as TaskManager<DownloadFileInfo>).getTotalSpeed(task.taskId);
          onProgress({
            speed: totalSpeed,
          });
        };

        const statusChangedFn = (value: TaskStatus) => {
          subTask.status = value;
          onProgress({ subTaskStatus: value });
          // PC-运行时子任务map清理工作
          if (value === TaskStatus.canceled || value === TaskStatus.paused) {
            (this as unknown as TaskManager<DownloadFileInfo>).deleteSubTaskInMap(subTask);
            removeEvents();
            next();
          }
        };

        const successFn = (successInfo: CoFile.DownloadFinishedParams) => {
          subTask.status = TaskStatus.success;
          if (!_.isUndefined(task.successNum) && !_.isUndefined(task.errorNum)) {
            task.successNum++;
            onProgress({
              progressText: `${task.successNum + task.errorNum}/${task.totalNum}`,
            });
          }
          removeEvents();
          next();
          (this as unknown as TaskManager<DownloadFileInfo>).deleteSubTaskInMap(subTask);
          // 下载成功上报
          reporter.report('download_finished', {
            download_type: successInfo.downloadType,
            download_result: 'successful',
          });
        };

        const errorFn = (err: any) => {
          subTask.status = TaskStatus.error;
          if (!_.isUndefined(task.successNum) && !_.isUndefined(task.errorNum)) {
            task.errorNum++;
            onProgress({
              progressText: `${task.successNum + task.errorNum}/${task.totalNum}`,
            });
          }
          logger.ui.error('[Download] error', JSON.stringify(transformErrorForSerialization(err)));
          subTask.error = err;
          (this as unknown as TaskManager<DownloadFileInfo>).deleteSubTaskInMap(subTask);
          // 下载失败上报
          reporter.report('download_finished', {
            download_type: err.downloadType,
            download_result: 'failed',
            status: err.status || 0,
            code: err.code || '',
            message: err.message || '',
          });
          removeEvents();
          // 处理错误：磁盘空间满,下载目录不存在,则直接中止当前队列且不往下进行
          if (['ENOSPC', 'ENOENT'].includes(err.code)) {
            next(err);
          } else {
            next();
          }
        };

        const taskIsStop = !TaskStatusUtil.isRunning(task.status) && !TaskStatusUtil.isError(task.status);
        const subTaskIsFinish = subTask.ins && !subTask.ins.isRunning() && subTask.status !== TaskStatus.paused;
        const subTaskIsSuccess = subTask.status === TaskStatus.success;
        const canSkip = taskIsStop || subTaskIsFinish || subTaskIsSuccess;

        if (canSkip) {
          removeEvents();
          next();
          return;
        }

        if (!subTask.ins) {
          subTask.ins = new Downloader(subTask.info);
        }
        bindEvnets();
        // start的参数restart只是个 info增加了变量，其实并没有用到，反而还会影响到ins的内存销毁
        subTask.ins.start();
        (this as unknown as TaskManager<DownloadFileInfo>).insertSubTaskInMap(subTask);
      },
      err => {
        setTimeout(() => {
          task.finishDate = Date.now();
          (this as unknown as TaskManager<DownloadFileInfo>).clearSubTaskInMap(task.taskId);
          if (err) {
            onError(err);
            return;
          }
          const { successNum = 0, errorNum = 0 } = task; // 获取的errorNum非+1后的，导致任务状态未变，加适当时延
          const finishNum = successNum + errorNum;
          if (!TaskStatusUtil.isRunning(task.status) || task.totalNum !== finishNum) {
            return;
          }
          const hasErr = (task.errorNum as number) > 0;
          if (hasErr) {
            onError(hasErr);
          } else {
            onFinish();
          }
        }, 200);
      },
    );
  },
  onPause(task: Task<DownloadFileInfo>) {
    // 停止任务的操作，例如停止该文件/文件夹的上传
    async.forEachLimit(task.getList(), 16, (subTask, next) => {
      const loader = subTask.ins as CommonLoader<DownloadFileInfo>;
      // 根据ins和status分别判断任务状态
      const insNotFinish = loader && !loader.isFinished();
      const statusNotFinish = ![TaskStatus.success, TaskStatus.error, TaskStatus.canceled].includes(subTask.status);
      const subTaskIsNotFinish = insNotFinish && statusNotFinish;
      if (subTaskIsNotFinish) {
        subTask.status = TaskStatus.paused;
        loader.pause();
        // subTask.ins = null;
      }
      next();
    });
  },
  onRetry(params: { task: Task<DownloadFileInfo>; onFinish: Function; onProgress: Function }) {
    const { task, onProgress, onFinish } = params;
    // 重试错误任务的操作
    async.forEachLimit(
      task.getList(),
      16,
      (subTask, next) => {
        if (subTask.status === TaskStatus.error) {
          delete subTask.ins;
        }
        next();
      },
      () => {
        task.errorNum = 0; // 重置错误进度
        const finishNum = task.successNum || 0 + task.errorNum;
        task &&
          onProgress({
            progressText: `${finishNum}/${task.totalNum}`,
          });
        onFinish();
      },
    );
  },
  onCancel(task: Task<DownloadFileInfo>) {
    // 这里进行取消任务的操作，例如取消该文件的下载
    async.forEachLimit(task.getList(), 16, (subTask, next) => {
      // 从本地恢复传输记录时没有loader 会影响删除本地临时文件
      if (!subTask.ins) {
        subTask.ins = new Downloader(subTask.info);
      }
      const loader = subTask.ins as CommonLoader<DownloadFileInfo>;
      if (loader?.isRunning() || loader?.isPaused()) {
        loader.cancel();
        subTask.ins = null;
      }
      next();
    });
  },
  onFinish(params: { list: Task<DownloadFileInfo>[] }) {
    // 所有任务都执行完毕
    const { list } = params;
  },
};
const downloadTaskManager = new TaskManager<DownloadFileInfo>(downloadOptions);
export default downloadTaskManager;
export async function handleDownloadFilesToTask(
  fileMap: Record<string, DownloadFileInfo[]>,
): Promise<Task<DownloadFileInfo>[]> {
  const formatSubTask = (files: DownloadFileInfo[], taskId: string) =>
    // eslint-disable-next-line implicit-arrow-linebreak
    async.mapLimit<DownloadFileInfo, SubTask<DownloadFileInfo>>(files, 16, (file, next) => {
      const subTask: SubTask<DownloadFileInfo> = {
        id: TaskManager.getTaskId('subTask_'),
        taskId,
        title: file.name,
        status: TaskStatus.waiting,
        percent: 0,
        ioPercent: 0,
        speed: 0,
        info: file,
        remainingTime: 0,
        remainingMessage: '',
      };
      next(null, subTask);
    });
  return async.mapLimit<string, Task<DownloadFileInfo>>(Object.keys(fileMap), 16, (mapKey, next) => {
    const taskId = TaskManager.getTaskId();
    const title = mapKey;
    const files = fileMap[mapKey];

    formatSubTask(files, taskId).then(subTask => {
      let totalSize = 0;
      files.forEach(file => {
        totalSize += file.size;
      });
      const task: Task<DownloadFileInfo> = {
        id: TaskManager.getTaskId('task_'),
        title,
        taskId,
        action: 'download',
        getList: () => subTask,
        getListLength: () => subTask.length,
        getItem: index => subTask[index],
        sortListByStatus: () => {
          subTask.sort((a, b) => {
            if (a.status === 'error' && b.status === 'success') return -1;
            if (a.status === 'success' && b.status === 'error') return 1;
            return 0;
          });
        },
        size: totalSize,
        status: TaskStatus.waiting,
        progressText: `0/${subTask.length}`,
        speed: 0,
        ioPercent: 0,
        finishDate: 0,
        error: undefined,
      };
      task.isDir = title !== files[0].name;
      next(null, task);
    });
  });
}

/**
 * @description 拼接文件的下载链接
 * @export
 * @param {(SMH.FileParams & {purpose?: string})} params
 * @return {*}  {Promise<string>}
 */
export async function getDownloadUrl(
  params: SMH.FileParams & {
    purpose?: string;
  },
): Promise<string> {
  const { spaceId, shareToken, filePath, contentDisposition, historyId, purpose, sharePreviewerOrganizationId } =
    params;
  const { libraryId, accessToken, userId } = await getSpaceAndUserInfo({
    spaceId,
    shareToken,
  });
  const historyIdPatch = historyId ? `&history_id=${historyId}` : '';
  const purposePatch = purpose ? `&purpose=${purpose}` : '';
  const contentDispositionPatch = contentDisposition ? `&content_disposition=${contentDisposition}` : '';
  const url = `${httpUrl.SMHUrl}/api/v1/file/${libraryId}/${spaceId}/${filePath}`;
  const queryStr = `?access_token=${accessToken}&user_id=${userId}${historyIdPatch}${purposePatch}${contentDispositionPatch}&share_previewer_organization_id=${sharePreviewerOrganizationId}`;
  const downloadUrl = `${url}${queryStr}`;
  return downloadUrl;
}
export async function getDataFromUrl<T>(url: string): Promise<T | null> {
  const request = getRequest({
    logPrefix: 'GetDataFromUrl',
  });
  try {
    const { data } = await request({
      url,
      method: 'get',
    });
    return data;
  } catch (error) {
    return null;
  }
}
